<template>
  <div class="stock-view-page">
    <StockDocumentPageHeading
      :title="$t('STOCK.PURCHASE_ORDERS.PAGE_TITLE')"
      :code="isNew ? '' : document.code"
      :new-label="isNew ? $t('STOCK.PURCHASE_ORDERS.NEW') : ''"
    />
    <v-card :loading="isProcessing" :disabled="isProcessing">
      <v-tabs v-model="tab" class="stock-color" left dark>
        <v-tab href="#header">{{ document.code || $t("STOCK.PURCHASE_ORDERS.NEW") }}</v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab d-flex align-center flex-wrap pa-2">
          <v-chip v-if="document.status" class="mr-2" :color="statusColor" small dark>
            {{ statusLabel }}
          </v-chip>
          <v-btn
            v-if="canConfirm"
            class="mr-2 success"
            small
            @click="confirmOrder"
          >
            {{ $t("STOCK.PURCHASE_ORDERS.CONFIRM") }}
          </v-btn>
          <v-btn
            v-if="canOrganizeTransport"
            class="mr-2 primary"
            small
            @click="organizeTransport"
          >
            {{ $t("STOCK.TRANSPORT_ORDERS.ORGANIZE_TRANSPORT") }}
          </v-btn>
          <v-btn
            v-if="canCreateReceipt"
            class="mr-2 primary"
            small
            @click="createReceipt"
          >
            {{ $t("STOCK.PURCHASE_ORDERS.CREATE_RECEIPT") }}
          </v-btn>
          <v-btn
            v-if="canRecordInTransit"
            class="mr-2 primary"
            small
            @click="recordInTransit"
          >
            {{ $t("STOCK.PURCHASE_ORDERS.RECORD_IN_TRANSIT") }}
          </v-btn>
          <v-btn
            v-if="canShort"
            class="mr-2 warning"
            small
            @click="shortOrder"
          >
            {{ $t("STOCK.PURCHASE_ORDERS.SHORT") }}
          </v-btn>
          <v-btn
            v-if="canCancel"
            class="mr-2 error"
            small
            @click="cancelOrder"
          >
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            v-if="canEditDocument"
            class="mr-2 success"
            small
            @click="save"
          >
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
        </div>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="header">
          <v-card flat>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.organization_id"
                    :items="organizations"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.WAREHOUSES.ORGANIZATION') + '*'"
                    :disabled="headerFieldDisabled('organization_id')"
                    :rules="headerFieldDisabled('organization_id') ? [] : [validations.required()]"
                    :error-messages="validationErrors.organization_id || []"
                    @change="clearHeaderValidationError('organization_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-switch
                    v-model="intercompanyOnly"
                    :label="$t('STOCK.PURCHASE_ORDERS.INTERCOMPANY')"
                    :hint="$t('STOCK.PURCHASE_ORDERS.INTERCOMPANY_HINT')"
                    persistent-hint
                    :disabled="!canEditDocument"
                    dense
                    hide-details="auto"
                    class="mt-0"
                    @change="onIntercompanyOnlyChange"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.supplier_company_id"
                    :items="filteredCompanyOptions"
                    :search-input.sync="companySearch"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.PURCHASE_ORDERS.SUPPLIER') + '*'"
                    :disabled="headerFieldDisabled('supplier_company_id')"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col
                  v-if="supplierRepresentsOrganization"
                  cols="12"
                  md="3"
                >
                  <v-autocomplete
                    v-model="document.counterparty_sales_order_id"
                    :items="counterpartySalesOrderOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.PURCHASE_ORDERS.COUNTERPARTY_SALES_ORDER')"
                    :hint="$t('STOCK.PURCHASE_ORDERS.COUNTERPARTY_SALES_ORDER_HINT')"
                    persistent-hint
                    :disabled="!canEditCounterpartyLink"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.destination_warehouse_id"
                    :items="warehouseOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.PURCHASE_ORDERS.DESTINATION_WAREHOUSE') + '*'"
                    :hint="$t('STOCK.PURCHASE_ORDERS.DESTINATION_WAREHOUSE_HINT')"
                    persistent-hint
                    :disabled="headerFieldDisabled('destination_warehouse_id')"
                    :rules="
                      headerFieldDisabled('destination_warehouse_id')
                        ? []
                        : [validations.required()]
                    "
                    :error-messages="validationErrors.destination_warehouse_id || []"
                    @change="clearHeaderValidationError('destination_warehouse_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.deal_id"
                    :items="dealOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.PURCHASE_ORDERS.DEAL')"
                    :disabled="headerFieldDisabled('deal_id')"
                    clearable
                    @change="onDealChange"
                  ></v-autocomplete>
                  <StockDealReference
                    :deal-id="document.deal_id"
                    :deal="document.deal || selectedDealOption"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="2">
                  <DatePicker
                    v-model="document.order_date"
                    :label="$t('STOCK.PURCHASE_ORDERS.ORDER_DATE') + '*'"
                    :disabled="headerFieldDisabled('order_date')"
                    without-hint
                  ></DatePicker>
                </v-col>
                <v-col cols="12" md="2">
                  <DatePicker
                    v-model="document.expected_delivery_date"
                    :label="$t('STOCK.PURCHASE_ORDERS.EXPECTED_DELIVERY')"
                    :disabled="headerFieldDisabled('expected_delivery_date')"
                    without-hint
                    clearable
                  ></DatePicker>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model.number="document.default_vat_rate"
                    type="number"
                    :label="$t('STOCK.PURCHASE_ORDERS.DEFAULT_VAT')"
                    suffix="%"
                    :disabled="headerFieldDisabled('default_vat_rate')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="document.currency_code"
                    :label="$t('STOCK.PURCHASE_ORDERS.CURRENCY')"
                    :disabled="headerFieldDisabled('currency_code')"
                  ></v-text-field>
                </v-col>
                <StockIncotermFields
                  :incoterm-id.sync="document.incoterm_id"
                  :incoterm-named-place.sync="document.incoterm_named_place"
                  :incoterm-options="incotermOptions"
                  :incoterm-disabled="headerFieldDisabled('incoterm_id')"
                  :named-place-disabled="headerFieldDisabled('incoterm_named_place')"
                />
                <v-col cols="12" md="3">
                  <v-textarea
                    v-model="document.notes"
                    rows="1"
                    auto-grow
                    :label="$t('GENERAL.LABELS.NOTES')"
                    :disabled="headerFieldDisabled('notes')"
                  ></v-textarea>
                </v-col>
              </v-row>

              <v-alert
                v-if="isAmendMode"
                type="info"
                dense
                outlined
                class="mb-4"
              >
                {{ $t("STOCK.PURCHASE_ORDERS.AMEND_HINT") }}
              </v-alert>

              <v-alert type="info" dense text class="mb-4">
                {{ $t("STOCK.PURCHASE_ORDERS.TOTALS_SUMMARY", {
                  net: formatNum(previewDocumentTotals.total_net),
                  vat: formatNum(previewDocumentTotals.total_vat),
                  gross: formatNum(previewDocumentTotals.total_gross),
                }) }}
              </v-alert>

              <v-subheader class="px-0">{{ $t("STOCK.PURCHASE_ORDERS.LINES") }}</v-subheader>
              
              <v-alert v-if="!lines.length" type="info" dense text class="mb-3">
                {{ $t("STOCK.PRODUCT_FINDER.EMPTY_LINES") }}
              </v-alert>
              <v-simple-table v-else dense class="stock-document-lines">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{{ $t("STOCK.PRODUCTS.PRODUCT") }}</th>
                    <th>{{ $t("STOCK.PURCHASE_ORDERS.QTY") }}</th>
                    <th>{{ $t("STOCK.PURCHASE_ORDERS.UNIT_COST_NET") }}</th>
                    <th>{{ $t("STOCK.PURCHASE_ORDERS.LINE_TOTAL_NET") }}</th>
                    <th>{{ $t("STOCK.PURCHASE_ORDERS.VAT") }}</th>
                    <th>{{ $t("STOCK.PURCHASE_ORDERS.FULFILLED") }}</th>
                    <th>{{ $t("STOCK.PURCHASE_ORDERS.REMAINING") }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(line, index) in lines"
                    :key="`line-${line.id || index}-${line.stock_product_id}`"
                  >
                    <td>{{ index + 1 }}</td>
                    <td class="stock-document-lines__product">
                      <div class="font-weight-medium">{{ lineProductLabel(line) }}</div>
                      <div class="text-caption grey--text">{{ lineProductMeta(line) }}</div>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="line.ordered_quantity"
                        type="number"
                        solo
                        dense
                        hide-details
                        class="stock-document-lines__field"
                        :suffix="lineUnitCode(line)"
                        :disabled="!canEditDocument"
                        :hint="lineQtyHint(line)"
                        persistent-hint
                        @focus="focusSelectAllInput"
                        @input="onLineQtyOrUnitInput(line, lineQuantityKey)"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="line.unit_cost_net"
                        type="number"
                        solo
                        dense
                        hide-details
                        class="stock-document-lines__field"
                        :suffix="document.currency_code || ''"
                        :disabled="!canEditDocument"
                        @focus="focusSelectAllInput"
                        @input="onLineQtyOrUnitInput(line, lineQuantityKey)"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="line.line_total_net"
                        type="number"
                        solo
                        dense
                        hide-details
                        class="stock-document-lines__field"
                        :suffix="document.currency_code || ''"
                        :disabled="!canEditDocument"
                        @focus="focusSelectAllInput"
                        @input="onLineTotalNetInput(line, lineQuantityKey)"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="line.vat_rate"
                        type="number"
                        solo
                        dense
                        hide-details
                        class="stock-document-lines__field stock-document-lines__field--narrow"
                        :disabled="!canEditDocument"
                        @focus="focusSelectAllInput"
                        @input="onLineVatRateInput"
                      ></v-text-field>
                    </td>
                    <td class="stock-document-lines__metric">
                      {{ fulfillmentFor(line).fulfilled }}
                    </td>
                    <td class="stock-document-lines__metric">
                      {{ fulfillmentFor(line).remaining }}
                    </td>
                    <td>
                      <v-btn
                        v-if="canEditDocument && canRemoveLine(line)"
                        icon
                        small
                        @click="removeLine(index)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                      <v-tooltip v-else-if="canEditDocument && lineHasReceipts(line)" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on">
                            <v-btn icon small disabled>
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </span>
                        </template>
                        <span>{{ $t("STOCK.PURCHASE_ORDERS.LINE_CANNOT_REMOVE") }}</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <StockProductLineFinder
                :disabled="!canEditDocument"
                @add="addProductFromFinder"
              />
              <v-subheader v-if="statusTimeline.length" class="px-0 mt-4">
                {{ $t("STOCK.PURCHASE_ORDERS.STATUS_TIMELINE") }}
              </v-subheader>
              <v-timeline v-if="statusTimeline.length" dense class="po-status-timeline">
                <v-timeline-item
                  v-for="entry in statusTimeline"
                  :key="entry.id"
                  small
                  :color="timelineEntryColor(entry)"
                >
                  <div class="subtitle-2">
                    {{ timelineEntryLabel(entry) }}
                  </div>
                  <div class="caption grey--text">
                    {{ formatTimelineDate(entry.changed_at) }}
                    <span v-if="entry.changed_by_user && entry.changed_by_user.name">
                      · {{ entry.changed_by_user.name }}
                    </span>
                  </div>
                  <div v-if="entry.notes" class="caption">{{ entry.notes }}</div>
                </v-timeline-item>
              </v-timeline>

              <StockPurchaseOrderMovementsTable
                v-if="!isNew"
                ref="poMovements"
                :purchase-order-id="purchaseOrderId"
              />

              <StockLinkedTransportOrdersTable
                v-if="!isNew"
                :items="transportOrders"
                :title="$t('STOCK.PURCHASE_ORDERS.RELATED_TRANSPORT_ORDERS')"
                :empty-message="$t('STOCK.PURCHASE_ORDERS.NO_RELATED_TRANSPORT_ORDERS')"
              />

              <StockDocumentFilesPanel
                v-if="!isNew"
                document-kind="purchase_order"
                :document-id="purchaseOrderId"
                :files="documentFiles"
                :can-regenerate="canRegenerateDocument"
                @files-updated="documentFiles = $event"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import { debounce } from "lodash";
import StockProductLineFinder from "@/views/partials/stock/StockProductLineFinder.vue";
import StockPurchaseOrderMovementsTable from "@/views/partials/stock/StockPurchaseOrderMovementsTable.vue";
import StockLinkedTransportOrdersTable from "@/views/partials/stock/StockLinkedTransportOrdersTable.vue";
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockDocumentFilesPanel from "@/views/partials/stock/StockDocumentFilesPanel.vue";
import StockDealReference from "@/views/partials/stock/StockDealReference.vue";
import StockIncotermFields from "@/views/partials/stock/StockIncotermFields.vue";
import DatePicker from "@/views/partials/form/DatePicker.vue";
import stockDocumentLinesMixin from "@/views/partials/stock/stockDocumentLines.mixin";
import stockDocumentOptionsMixin from "@/views/partials/stock/stockDocumentOptions.mixin";
import stockDocumentTotalsMixin from "@/views/partials/stock/stockDocumentTotals.mixin";
import stockDocumentContextMixin from "@/views/partials/stock/stockDocumentContext.mixin";
import stockIncotermMixin from "@/views/partials/stock/stockIncoterm.mixin";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import {
  GET_STOCK_PURCHASE_ORDER,
  CREATE_STOCK_PURCHASE_ORDER,
  UPDATE_STOCK_PURCHASE_ORDER,
  CONFIRM_STOCK_PURCHASE_ORDER,
  CANCEL_STOCK_PURCHASE_ORDER,
  GET_STOCK_PO_COUNTERPARTY_SO_OPTIONS,
  RECORD_PO_IN_TRANSIT,
} from "@/store/stock/stockPurchaseOrders.module";
import { GET_STOCK_DEAL_OPTIONS } from "@/store/stock/stockReceipts.module";
import { GET_STOCK_PO_DRAFT_FROM_SO } from "@/store/stock/stockSalesOrders.module";
import { SHOW_SNACK } from "@/store/snack.module";

const emptyLine = (vat) => ({
  stock_product_id: null,
  ordered_quantity: 1,
  unit_id: null,
  unit_code: null,
  unit_cost_net: 0,
  line_total_net: 0,
  vat_rate: vat,
});

export default {
  name: "StockViewPurchaseOrder",
  components: {
    StockProductLineFinder,
    StockPurchaseOrderMovementsTable,
    StockLinkedTransportOrdersTable,
    StockDocumentPageHeading,
    StockDocumentFilesPanel,
    StockDealReference,
    StockIncotermFields,
    DatePicker,
  },
  mixins: [
    stockDocumentLinesMixin,
    stockDocumentOptionsMixin,
    stockDocumentTotalsMixin,
    stockDocumentContextMixin,
    stockIncotermMixin,
  ],
  data() {
    return {
      lineQuantityKey: "ordered_quantity",
      tab: "header",
      isProcessing: false,
      document: this.emptyDocument(),
      lines: [],
      fulfillment: {},
      transportOrders: [],
      organizations: [],
      companyOptions: [],
      intercompanyOnly: false,
      counterpartySalesOrderOptions: [],
      warehouseOptions: [],
      dealOptions: [],
      companySearch: null,
      validationErrors: {},
      documentFiles: [],
    };
  },
  computed: {
    isNew() {
      return !this.$route.params.id;
    },
    selectedSupplierOrganizationId() {
      const supplier = (this.companyOptions || []).find(
        (company) => company.id === this.document.supplier_company_id
      );
      return supplier?.organization_id || null;
    },
    supplierRepresentsOrganization() {
      return Boolean(this.selectedSupplierOrganizationId);
    },
    filteredCompanyOptions() {
      const options = this.companyOptions || [];
      if (!this.intercompanyOnly) {
        return options;
      }
      return options.filter((company) => company.organization_id);
    },
    canEditCounterpartyLink() {
      return this.canEditDocument;
    },
    isAmendMode() {
      return ["confirmed", "partially_received"].includes(this.document.status);
    },
    canEditDocument() {
      if (this.isNew) {
        return this.hasPermissions("stock-purchase-orders-create");
      }
      const editableStatus = ["draft", "sent", "confirmed", "partially_received"].includes(
        this.document.status
      );
      return editableStatus && this.hasPermissions("stock-purchase-orders-edit");
    },
    canSave() {
      return this.canEditDocument;
    },
    canConfirm() {
      return (
        !this.isNew &&
        ["draft", "sent"].includes(this.document.status) &&
        this.hasPermissions("stock-purchase-orders-confirm")
      );
    },
    canCreateReceipt() {
      return (
        !this.isNew &&
        ["confirmed", "partially_received"].includes(this.document.status) &&
        this.hasPermissions("stock-receipts-create")
      );
    },
    canRecordInTransit() {
      // Deprecated in Phase 6.5 — open PO lines feed MRP timeline instead of transit expected receipts.
      return false;
    },
    canOrganizeTransport() {
      return (
        !this.isNew &&
        ["confirmed", "partially_received"].includes(this.document.status) &&
        this.hasPermissions("stock-transport-orders-create")
      );
    },
    canRegenerateDocument() {
      return ["confirmed", "partially_received", "received", "invoiced", "closed"].includes(
        this.document.status
      );
    },
    canCancel() {
      return (
        !this.isNew &&
        !["cancelled", "received", "closed"].includes(this.document.status) &&
        this.hasPermissions("stock-purchase-orders-cancel")
      );
    },
    canShort() {
      if (!this.isAmendMode || !this.canEditDocument) {
        return false;
      }
      const hasReceived = this.lines.some((line) => this.lineFulfilledQty(line) > 0.0001);
      const hasRemainder = this.lines.some((line) => this.lineRemainingOnForm(line) > 0.0001);
      return hasReceived && hasRemainder;
    },
    statusColor() {
      const map = {
        draft: "grey",
        confirmed: "primary",
        partially_received: "orange",
        received: "success",
        cancelled: "error",
      };
      return map[this.document.status] || "grey";
    },
    statusLabel() {
      const key = `STOCK.STATUSES.PO.${(this.document.status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : this.document.status;
    },
    purchaseOrderId() {
      const id = this.$route.params.id;
      return id ? Number(id) : null;
    },
    statusTimeline() {
      const timeline = this.document.status_timeline;
      if (!Array.isArray(timeline)) {
        return [];
      }
      return [...timeline].sort(
        (a, b) => new Date(b.changed_at) - new Date(a.changed_at)
      );
    },
  },
  watch: {
    companySearch: debounce(function () {
      this.loadCompanies();
    }, 300),
    "document.organization_id"() {
      this.runStockHeaderReloadOnOrganizationChange();
      this.loadCounterpartySalesOrderOptions();
    },
    "document.supplier_company_id"() {
      this.loadCounterpartySalesOrderOptions();
    },
    "document.default_vat_rate"(value) {
      this.lines.forEach((line) => {
        if (line.vat_rate === null || line.vat_rate === undefined) {
          line.vat_rate = value;
        }
      });
    },
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    emptyDocument() {
      return {
        organization_id: null,
        supplier_company_id: null,
        source_sales_order_id: null,
        counterparty_sales_order_id: null,
        destination_warehouse_id: null,
        deal_id: null,
        order_date: new Date().toISOString().slice(0, 10),
        expected_delivery_date: null,
        default_vat_rate: 20,
        currency_code: "EUR",
        exchange_rate_to_base: 1,
        incoterm_id: null,
        incoterm_named_place: null,
        notes: null,
        status: "draft",
        code: null,
        total_net: 0,
        total_vat: 0,
        total_gross: 0,
      };
    },
    async bootstrap() {
      this.isProcessing = true;
      try {
        await this.loadOrganizations();
        await this.loadCompanies();
        await this.loadWarehouses();
        await this.loadDeals();
        await this.loadIncoterms();

        if (!this.isNew) {
          const data = await this.$store.dispatch(
            GET_STOCK_PURCHASE_ORDER,
            this.$route.params.id
          );
          await this.applyDocument(data);
        } else {
          await this.withSuppressedStockHeaderReload(async () => {
            if (this.hasStockDraftSourceQuery()) {
              await this.loadDraftFromQuery();
            } else {
              this.applyStockContextDefaultsOnNewDocument();
            }
          });
        }
      } finally {
        this.isProcessing = false;
      }
    },
    async loadDraftFromQuery() {
      const query = this.$route.query || {};
      const salesOrderId = query.sales_order_id;
      const supplierCompanyId = query.supplier_company_id;

      if (!salesOrderId || !supplierCompanyId) {
        return;
      }

      try {
        const data = await this.$store.dispatch(GET_STOCK_PO_DRAFT_FROM_SO, {
          id: salesOrderId,
          params: {
            supplier_company_id: supplierCompanyId,
          },
        });
        await this.applyDocument(data);
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      }
    },
    async applyDocument(data) {
      await this.withSuppressedStockHeaderReload(async () => {
        this.document = {
          ...this.emptyDocument(),
          ...data,
          ...this.mapPurchaseOrderDocument(data),
        };
        await this.reloadPurchaseOrderHeaderOptions();
        if (this.isNew) {
          this.setHeaderOptionsSeed(data, "syncPurchaseOrderHeaderOptions");
        } else {
          this.clearHeaderOptionsSeed();
        }
        this.syncPurchaseOrderHeaderOptions(data);
      });
      this.fulfillment = data.fulfillment || {};
      this.transportOrders = Array.isArray(data.transport_orders) ? data.transport_orders : [];
      this.lines = (data.items || []).map((item) => {
        const line = this.mapLineFromApi(item, data, this.lineQuantityKey);
        line.id = item.id;
        line.fulfillment =
          item.fulfillment ??
          this.fulfillment[item.id] ??
          this.fulfillment[String(item.id)] ??
          null;
        return line;
      });
      this.documentFiles = Array.isArray(data.files) ? data.files : [];
      this.syncIncotermFromDocument(data);
      this.syncIntercompanyFlagFromSupplier(data);
      this.$nextTick(() => this.$refs.poMovements?.reload());
    },
    payload() {
      return {
        ...this.document,
        items: this.lines
          .filter((line) => line.stock_product_id)
          .map((line, index) => ({
            id: line.id || undefined,
            line_no: index + 1,
            stock_product_id: line.stock_product_id,
            ordered_quantity: line.ordered_quantity,
            unit_id: line.unit_id,
            unit_cost_net: line.unit_cost_net,
            vat_rate: line.vat_rate ?? this.document.default_vat_rate,
          })),
      };
    },
    async save() {
      if (!this.validateOrganizationAndWarehouseRequired()) {
        return;
      }
      this.isProcessing = true;
      try {
        this.validationErrors = {};
        const params = this.payload();
        let data;
        if (this.isNew) {
          data = await this.$store.dispatch(CREATE_STOCK_PURCHASE_ORDER, params);
          this.$router.replace({
            name: "stock-purchase-orders-view",
            params: { id: data.id },
          });
        } else {
          data = await this.$store.dispatch(UPDATE_STOCK_PURCHASE_ORDER, {
            id: this.$route.params.id,
            params,
          });
        }
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.validationErrors = (errors && errors.errors) || {};
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async confirmOrder() {
      if (!this.validateOrganizationAndWarehouseRequired()) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(
          CONFIRM_STOCK_PURCHASE_ORDER,
          this.$route.params.id
        );
        await this.applyDocument(data);
      } catch (errors) {
        this.validationErrors = (errors && errors.errors) || {};
        await this.reloadSavedDocument();
      } finally {
        this.isProcessing = false;
      }
    },
    async reloadSavedDocument() {
      if (this.isNew) {
        return;
      }
      try {
        const data = await this.$store.dispatch(
          GET_STOCK_PURCHASE_ORDER,
          this.$route.params.id
        );
        await this.applyDocument(data);
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      }
    },
    async cancelOrder() {
      if (!confirm(this.$t("STOCK.PURCHASE_ORDERS.CONFIRM_CANCEL"))) return;
      const data = await this.$store.dispatch(
        CANCEL_STOCK_PURCHASE_ORDER,
        this.$route.params.id
      );
      await this.applyDocument(data);
    },
    async shortOrder() {
      if (!this.canShort) {
        return;
      }
      if (!confirm(this.$t("STOCK.PURCHASE_ORDERS.CONFIRM_SHORT"))) {
        return;
      }
      this.lines = this.lines.filter((line) => {
        const fulfilled = this.lineFulfilledQty(line);
        if (fulfilled <= 0.0001) {
          return false;
        }
        line.ordered_quantity = fulfilled;
        this.onLineQtyOrUnitInput(line, this.lineQuantityKey);
        return true;
      });
      await this.save();
    },
    async organizeTransport() {
      this.$router.push({
        name: "stock-transport-orders-create",
        query: {
          purchase_order_id: this.$route.params.id,
          direction: "inbound",
          deal_id: this.document.deal_id || undefined,
          organization_id: this.document.organization_id || undefined,
        },
      });
    },
    createReceipt() {
      if (!this.document.destination_warehouse_id) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.PURCHASE_ORDERS.WAREHOUSE_REQUIRED_RECEIPT"),
          color: "error",
        });
        return;
      }
      this.$router.push({
        name: "stock-receipts-create",
        query: {
          purchase_order_id: this.$route.params.id,
          deal_id: this.document.deal_id || undefined,
          organization_id: this.document.organization_id || undefined,
        },
      });
    },
    recordInTransit() {
      if (
        !confirm(this.$t("STOCK.PURCHASE_ORDERS.RECORD_IN_TRANSIT_CONFIRM"))
      ) {
        return;
      }

      this.isProcessing = true;
      this.$store
        .dispatch(RECORD_PO_IN_TRANSIT, { id: this.$route.params.id })
        .then(async (receipt) => {
          const data = await this.$store.dispatch(
            GET_STOCK_PURCHASE_ORDER,
            this.$route.params.id
          );
          await this.applyDocument(data);
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("STOCK.PURCHASE_ORDERS.RECORD_IN_TRANSIT_SUCCESS"),
            color: "success",
            timeout: 8000,
          });
          if (receipt && receipt.id) {
            this.$router.push({
              name: "stock-receipts-view",
              params: { id: receipt.id },
            });
          }
        })
        .catch((errors) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
            color: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    syncIntercompanyFlagFromSupplier(data) {
      const supplier =
        data?.supplier_company
        || (this.companyOptions || []).find(
          (company) => Number(company.id) === Number(this.document.supplier_company_id)
        );
      this.intercompanyOnly = Boolean(supplier?.organization_id);
    },
    onIntercompanyOnlyChange(enabled) {
      if (!enabled || !this.document.supplier_company_id) {
        return;
      }
      const supplier = (this.companyOptions || []).find(
        (company) => Number(company.id) === Number(this.document.supplier_company_id)
      );
      if (supplier && !supplier.organization_id) {
        this.document.supplier_company_id = null;
        this.document.counterparty_sales_order_id = null;
      }
    },
    addProductFromFinder(product) {
      this.addProductLine(product, emptyLine);
    },
    removeLine(index) {
      this.lines.splice(index, 1);
    },
    headerFieldDisabled(field) {
      if (!this.canEditDocument) {
        return true;
      }
      if (!this.isAmendMode) {
        return false;
      }
      if (["deal_id", "expected_delivery_date", "notes", "incoterm_id", "incoterm_named_place", "destination_warehouse_id"].includes(field)) {
        return false;
      }
      return true;
    },
    lineFulfilledQty(line) {
      const row = line.fulfillment || {};
      return Number(row.fulfilled_quantity || 0);
    },
    lineRemainingOnForm(line) {
      const ordered = Number(line.ordered_quantity || 0);
      return Math.max(0, ordered - this.lineFulfilledQty(line));
    },
    lineHasReceipts(line) {
      return this.lineFulfilledQty(line) > 0.0001;
    },
    canRemoveLine(line) {
      return !this.lineHasReceipts(line);
    },
    lineQtyHint(line) {
      if (!this.isAmendMode || !this.lineHasReceipts(line)) {
        return "";
      }
      return `min ${this.formatNum(this.lineFulfilledQty(line))}`;
    },
    fulfillmentFor(line) {
      const row =
        line.fulfillment ||
        this.fulfillment[line.id] ||
        this.fulfillment[String(line.id)] ||
        {};
      const formatQty = (value) =>
        value != null && value !== "" ? this.formatNum(value) : "-";
      return {
        fulfilled: formatQty(row.fulfilled_quantity),
        remaining: formatQty(row.remaining_quantity),
      };
    },
    timelineEntryLabel(entry) {
      const slug = entry.status && entry.status.slug ? entry.status.slug : "";
      const key = `STOCK.STATUSES.PO.${slug.toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : (entry.status && entry.status.name) || slug || "-";
    },
    timelineEntryColor(entry) {
      const slug = entry.status && entry.status.slug;
      const map = {
        draft: "grey",
        sent: "grey",
        confirmed: "primary",
        partially_received: "orange",
        received: "success",
        cancelled: "error",
      };
      return map[slug] || "grey";
    },
    formatTimelineDate(value) {
      if (!value) {
        return "-";
      }
      return new Date(value).toLocaleString();
    },
    async loadCompanies() {
      const data = await this.$store.dispatch(GET_COMPANIES_OPTIONS, {
        search: this.companySearch,
      });
      this.companyOptions = data.data || data;
      this.preserveDealPartyCompaniesInOptions();
    },
    async loadCounterpartySalesOrderOptions() {
      if (!this.supplierRepresentsOrganization || !this.document.organization_id) {
        this.counterpartySalesOrderOptions = [];
        return;
      }

      try {
        this.counterpartySalesOrderOptions = await this.$store.dispatch(
          GET_STOCK_PO_COUNTERPARTY_SO_OPTIONS,
          {
            organization_id: this.document.organization_id,
            supplier_company_id: this.document.supplier_company_id,
          }
        );
      } catch (error) {
        this.counterpartySalesOrderOptions = [];
      }
    },
    async loadDeals() {
      const responseData = await this.$store.dispatch(GET_STOCK_DEAL_OPTIONS, {
        organization_id: this.document.organization_id,
        limit: 50,
      });
      this.applyDealOptionsResponse(responseData);
    },
    formatNum(value) {
      return Number(value || 0).toFixed(2);
    },
  },
};
</script>

<style scoped>
.stock-view-page {
  padding-bottom: 48px;
  margin-bottom: 24px;
}
.stock-document-lines th,
.stock-document-lines td {
  vertical-align: middle !important;
}
.stock-document-lines__product {
  min-width: 240px;
  max-width: 360px;
}
.stock-document-lines__field {
  max-width: 130px;
}
.stock-document-lines__field--narrow {
  max-width: 90px;
}
.stock-document-lines__metric {
  min-width: 88px;
  text-align: right;
  white-space: nowrap;
}
</style>
